import { useState } from "react";
import { Pencil } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button, useToast } from "../../../components";
import FormHeader from "../../../components/form/FormHeader";
import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import { Input } from "../../../components/input";
import MerchantDetailsView from "../components/MerchantDetailsView";
import { suspendMerchantSchema } from "../components/merchant-form/merchant.scheme";
import { useApproveMerchant, useGetDetailMerchant, useSuspendMerchant } from "../hooks";
import { getMerchantDisplayName, getMerchantStatus } from "../utils/merchant.utils";
import { getApiErrorMessage } from "../../../lib/result-codes";

type MerchantDetailsPageProps = {
  basePath?: string;
};

export default function MerchantDetailsPage({
  basePath = "/merchant-management",
}: MerchantDetailsPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    merchant,
    loading: isLoadingMerchant,
    error: merchantError,
    refetch,
  } = useGetDetailMerchant(id);
  const { approveMerchant, loading: isApproving } = useApproveMerchant();
  const { suspendMerchant, loading: isSuspending } = useSuspendMerchant();
  const { addToast } = useToast();
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendReasonError, setSuspendReasonError] = useState<string | null>(null);

  if (isLoadingMerchant && !merchant) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
          <p className="text-sm text-gray-600 dark:text-gray-300">{t("merchant.loading_detail")}</p>
        </div>
      </div>
    );
  }

  if (merchantError && !merchant) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <p className="mb-4 text-sm text-red-600 dark:text-red-400">{merchantError}</p>
          <Button
            onClick={() => navigate(basePath)}
            type="button"
          >
            {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  if (!merchant) return null;

  const status = getMerchantStatus(merchant);
  const canEdit = status === "pending" || status === "verified";

  const handleConfirmApprove = async () => {
    try {
      await approveMerchant(merchant.id);
      await refetch();
      addToast({
        type: "success",
        title: "Success!",
        message: t("merchant.approve_success"),
      });
      setShowApproveDialog(false);
    } catch (approveError: unknown) {
      addToast({
        type: "error",
        title: "Error!",
        message: getApiErrorMessage(approveError, t("merchant.approve_error")),
      });
    }
  };

  const handleConfirmSuspend = async () => {
    const parsed = suspendMerchantSchema.safeParse({ reason: suspendReason });
    if (!parsed.success) {
      setSuspendReasonError(parsed.error.issues[0]?.message ?? t("merchant.reason_required"));
      return;
    }
    setSuspendReasonError(null);

    try {
      await suspendMerchant(merchant.id, parsed.data.reason);
      await refetch();
      addToast({
        type: "success",
        title: "Success!",
        message: t("merchant.suspend_success"),
      });
      setShowSuspendDialog(false);
      setSuspendReason("");
    } catch (suspendError: unknown) {
      addToast({
        type: "error",
        title: "Error!",
        message: getApiErrorMessage(suspendError, t("merchant.suspend_error")),
      });
    }
  };

  return (
    <>
      <FormHeader
        onBack={() => navigate(basePath)}
        title={getMerchantDisplayName(merchant)}
        subtitle={t(`merchant.${status}`)}
        backLabel={t("common.back")}
        actions={
          <div className="flex items-center gap-2">
            {status === "pending" ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowApproveDialog(true)}
                disabled={isApproving || isSuspending}
              >
                {t("common.approve")}
              </Button>
            ) : null}
            {status !== "suspended" ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowSuspendDialog(true);
                  setSuspendReason("");
                  setSuspendReasonError(null);
                }}
                disabled={isApproving || isSuspending}
              >
                {t("merchant.suspend")}
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                type="button"
                onClick={() => navigate(`${basePath}/update/${merchant.id}`)}
                className="text-white"
              >
                <Pencil aria-hidden="true" />
                {t("merchant.edit_merchant")}
              </Button>
            ) : undefined}
          </div>
        }
      />
      <MerchantDetailsView merchant={merchant} />

      <ConfirmationDialog
        isOpen={showApproveDialog}
        onClose={() => setShowApproveDialog(false)}
        onConfirm={handleConfirmApprove}
        title={t("merchant.approve_merchant")}
        description={t("merchant.approve_confirm", { name: getMerchantDisplayName(merchant) })}
        confirmText={t("common.approve")}
        cancelText={t("common.cancel")}
        isLoading={isApproving}
        type="info"
      />

      <ConfirmationDialog
        isOpen={showSuspendDialog}
        onClose={() => {
          setShowSuspendDialog(false);
          setSuspendReason("");
          setSuspendReasonError(null);
        }}
        onConfirm={handleConfirmSuspend}
        title={t("merchant.suspend_merchant")}
        description={
          <div className="flex flex-col gap-2 text-left">
            <span>{t("merchant.suspend_confirm", { name: getMerchantDisplayName(merchant) })}</span>
            <Input
              name="reason"
              value={suspendReason}
              onChange={(event) => setSuspendReason(event.target.value)}
              placeholder={t("merchant.reason_placeholder")}
              className="rounded-xl"
            />
            {suspendReasonError ? (
              <span className="text-sm text-red-600 dark:text-red-400">{suspendReasonError}</span>
            ) : null}
          </div>
        }
        confirmText={t("merchant.suspend")}
        cancelText={t("common.cancel")}
        isLoading={isSuspending}
        type="warning"
      />
    </>
  );
}
